/**
 * @module flow-list/utils
 * @see apps/web/src/ui/pages/FlowList.tsx
 */

import type { StatusTone, TestStatus, BadgeVariant } from '@chatsdet/ui';
import type { WorkflowRunDto, WorkflowTestRunDto } from './types';
import type { WorkflowStatus } from '../../../hooks/useWorkflows';
import { formatUpdatedAt } from '../FlowDesigner/utils';

export const workflowStatusBadgeVariant: Record<WorkflowStatus, BadgeVariant> = {
  draft: 'secondary',
  approved: 'success',
  generating: 'default',
  building: 'default',
  deleted: 'destructive',
  diagnostic: 'destructive',
};

export function isRunnableFromFlowList(status: WorkflowStatus): boolean {
  return status === 'draft' || status === 'approved';
}

export function formatWorkflowStatus(status: WorkflowStatus): string {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'approved':
      return 'Approved';
    case 'generating':
      return 'Generating';
    case 'building':
      return 'Building';
    case 'deleted':
      return 'Deleted';
    case 'diagnostic':
      return 'Diagnostic';
    default:
      return status;
  }
}

function _formatTestRunStatus(status: WorkflowTestRunDto['status']) {
  switch (status) {
    case 'queued':
      return 'Queued';
    case 'running':
      return 'Running';
    case 'success':
      return 'Passed';
    case 'failed':
      return 'Failed';
    case 'skipped':
      return 'Skipped';
    default:
      return status;
  }
}

export function formatTooltipTimestamp(value: string | null) {
  if (!value) return 'Not available';
  return formatUpdatedAt(value);
}

const _testRunStatusTone: Record<WorkflowTestRunDto['status'], StatusTone> = {
  queued: 'muted',
  running: 'warning',
  success: 'success',
  failed: 'danger',
  inconclusive: 'muted',
  skipped: 'muted',
};

function getTestRunTimestamp(run: WorkflowTestRunDto) {
  return run.finishedAt ?? run.startedAt ?? run.createdAt;
}

function _getTestRunTimestampMs(run: WorkflowTestRunDto) {
  const timestamp = getTestRunTimestamp(run);
  return timestamp ? new Date(timestamp).getTime() : 0;
}

export function formatWorkflowRunStatus(status: WorkflowRunDto['status']) {
  switch (status) {
    case 'queued':
      return 'Queued';
    case 'running':
      return 'Running';
    case 'success':
      return 'Passed';
    case 'failed':
      return 'Failed';
    case 'canceled':
      return 'Canceled';
    case 'timeout':
      return 'Timed Out';
    default:
      return status;
  }
}

export const workflowRunStatusTone: Record<WorkflowRunDto['status'], StatusTone> = {
  queued: 'muted',
  running: 'warning',
  success: 'success',
  failed: 'danger',
  inconclusive: 'muted',
  canceled: 'muted',
  // Timeouts count as failures, matching health classification (a flow that
  // times out repeatedly degrades rather than reading as "not a verdict").
  timeout: 'danger',
};

export function getWorkflowRunTimestamp(run: WorkflowRunDto) {
  return run.finishedAt ?? run.startedAt ?? run.createdAt;
}

export const workflowRunStatusToTestStatus: Record<WorkflowRunDto['status'], TestStatus> = {
  queued: 'queued',
  running: 'running',
  success: 'success',
  failed: 'failed',
  inconclusive: 'inconclusive',
  canceled: 'canceled',
  timeout: 'failed',
};
